// Auction types
export const ENGLISH_TYPE = 'english';
export const REVERSE_TYPE = 'reverse';
export const INSIDER_TYPE = 'insider';
export const TEXAS_TYPE = 'texas';
export const ENGLISH_REVERSE_CONTINUOUS_TYPE = 'continuous';
export const SEALED_TYPE = 'sealed';
export const MULTILOT_TYPE = 'multilot';

// Auction statuses
export const ACTIVE_TENDERING_STATUS = 'active.tendering';
export const ACTIVE_AUCTION_STATUS = 'active.auction';
export const UNSUCCESSFUL_STATUS = 'unsuccessful';
export const COMPLETE_STATUS = 'complete';

export const AUCTION_TYPE_LABELS = {
  [ENGLISH_TYPE]: 'Forward English auction (Round-based)',
  [REVERSE_TYPE]: 'Reverse English auction (Round-based)',
  [ENGLISH_REVERSE_CONTINUOUS_TYPE]: 'English Auction',
  [INSIDER_TYPE]: 'Hybrid Dutch auction (Insider)',
  [TEXAS_TYPE]: 'Texas auction',
  [SEALED_TYPE]: 'Sealed-bid auction',
  [MULTILOT_TYPE]: 'Multilot auction',
};

export const AUCTION_TYPE_OPTIONS = [
  {
    text: AUCTION_TYPE_LABELS[ENGLISH_REVERSE_CONTINUOUS_TYPE],
    value: ENGLISH_REVERSE_CONTINUOUS_TYPE,
  },
  { text: AUCTION_TYPE_LABELS[ENGLISH_TYPE], value: ENGLISH_TYPE },
  { text: AUCTION_TYPE_LABELS[REVERSE_TYPE], value: REVERSE_TYPE },
  { text: AUCTION_TYPE_LABELS[INSIDER_TYPE], value: INSIDER_TYPE },
  { text: AUCTION_TYPE_LABELS[TEXAS_TYPE], value: TEXAS_TYPE },
  { text: AUCTION_TYPE_LABELS[SEALED_TYPE], value: SEALED_TYPE },
  { text: AUCTION_TYPE_LABELS[MULTILOT_TYPE], value: MULTILOT_TYPE },
];

export const AUCTION_STATUS_LABELS = {
  [ACTIVE_TENDERING_STATUS]: 'Awaiting',
  [ACTIVE_AUCTION_STATUS]: 'In Progress',
  [UNSUCCESSFUL_STATUS]: 'Failed',
  [COMPLETE_STATUS]: 'Successful',
};

export const AUCTION_STATUS_OPTIONS = [
  { text: AUCTION_STATUS_LABELS[ACTIVE_TENDERING_STATUS], value: ACTIVE_TENDERING_STATUS },
  { text: AUCTION_STATUS_LABELS[ACTIVE_AUCTION_STATUS], value: ACTIVE_AUCTION_STATUS },
  { text: AUCTION_STATUS_LABELS[COMPLETE_STATUS], value: COMPLETE_STATUS },
  { text: AUCTION_STATUS_LABELS[UNSUCCESSFUL_STATUS], value: UNSUCCESSFUL_STATUS },
];

export const DEFAULT_AUCTION_STATUS_FILTER = [
  ACTIVE_TENDERING_STATUS,
  ACTIVE_AUCTION_STATUS,
  COMPLETE_STATUS,
  UNSUCCESSFUL_STATUS,
];
export const DEFAULT_AUCTION_TYPE_FILTER = [
  ENGLISH_TYPE,
  ENGLISH_REVERSE_CONTINUOUS_TYPE,
  INSIDER_TYPE,
  REVERSE_TYPE,
  TEXAS_TYPE,
  SEALED_TYPE,
  MULTILOT_TYPE,
];

export const isEnglishReverseContinuousType = (type) => type === ENGLISH_REVERSE_CONTINUOUS_TYPE;

export const isReverseLikeType = (type) =>
  [REVERSE_TYPE, ENGLISH_REVERSE_CONTINUOUS_TYPE, SEALED_TYPE, MULTILOT_TYPE].includes(type);
export const isInsiderOrTexasType = (type) => [INSIDER_TYPE, TEXAS_TYPE].includes(type);
export const isMultilotType = (type) => type === MULTILOT_TYPE;
export const isBidValueRequiredType = (type) =>
  !isInsiderOrTexasType(type) &&
  !isEnglishReverseContinuousType(type) &&
  !isMultilotType(type);
export const isTenderingOrAuctionStatus = (status) =>
  [ACTIVE_TENDERING_STATUS, ACTIVE_AUCTION_STATUS].includes(status);

/** v-data-table `value` keys and sortBy field names for the auction list */
export const AUCTION_LIST_COLUMN = {
  TITLE: 'title',
  STATUS: 'status',
  AUCTION_DATE: 'auctionDate',
  PROCUREMENT_METHOD_TYPE: 'procurementMethodType',
  STARTING_PRICE: 'startingPrice',
};

// Shared auction UI labels
export const AUCTION_LABEL_TITLE = 'Title';
export const AUCTION_LABEL_STATUS = 'Status';
export const AUCTION_LABEL_STARTING_DATE = 'Starting Date';
export const AUCTION_LABEL_TYPE = 'Type';
export const AUCTION_LABEL_STARTING_PRICE = 'Starting Price';
export const AUCTION_LABEL_ANTI_SNIPING_WINDOW = 'Anti-sniping window';

/** Table header copy and filter placeholders (must stay in sync) */
export const AUCTION_LIST_HEADER_LABELS = {
  TITLE: AUCTION_LABEL_TITLE,
  STATUS: AUCTION_LABEL_STATUS,
  STARTING_DATE: AUCTION_LABEL_STARTING_DATE,
  TYPE: AUCTION_LABEL_TYPE,
  STARTING_PRICE: AUCTION_LABEL_STARTING_PRICE,
};

/** Share of total width for list table + filter row (must sum to 100%). Tune if content breaks layout. */
export const AUCTION_LIST_HEADERS = [
  {
    text: AUCTION_LIST_HEADER_LABELS.TITLE,
    value: AUCTION_LIST_COLUMN.TITLE,
    width: '24%',
  },
  {
    text: AUCTION_LIST_HEADER_LABELS.STATUS,
    value: AUCTION_LIST_COLUMN.STATUS,
    width: '11%',
  },
  {
    text: AUCTION_LIST_HEADER_LABELS.STARTING_DATE,
    value: AUCTION_LIST_COLUMN.AUCTION_DATE,
    width: '13%',
  },
  {
    text: AUCTION_LIST_HEADER_LABELS.TYPE,
    value: AUCTION_LIST_COLUMN.PROCUREMENT_METHOD_TYPE,
    width: '34%',
  },
  {
    text: AUCTION_LIST_HEADER_LABELS.STARTING_PRICE,
    value: AUCTION_LIST_COLUMN.STARTING_PRICE,
    width: '18%',
  },
];

/** Maps v-data-table sort key → API `order_by` query value */
export const AUCTION_LIST_ORDER_BY_API = {
  [AUCTION_LIST_COLUMN.TITLE]: 'title',
  [AUCTION_LIST_COLUMN.STATUS]: 'status',
  [AUCTION_LIST_COLUMN.AUCTION_DATE]: 'auction_period_start_date',
  [AUCTION_LIST_COLUMN.STARTING_PRICE]: 'value_amount',
  [AUCTION_LIST_COLUMN.PROCUREMENT_METHOD_TYPE]: 'procurement_method_type',
};

/** When no column sort is active (`sortBy` falsy), API defaults to ordering like the Status column */
export const AUCTION_LIST_DEFAULT_ORDER_BY =
  AUCTION_LIST_ORDER_BY_API[AUCTION_LIST_COLUMN.STATUS];

export const AUCTION_DETAIL_TITLE_LABELS = {
  EDIT_ACTION: 'Edit Auction',
  WIDGET_COUNTDOWN: 'Countdown to the Auction start',
  WIDGET_STATUS_PREFIX: 'Auction - ',
};

export const AUCTION_DETAIL_FIELD_LABEL_TYPE = AUCTION_LABEL_TYPE;
export const AUCTION_DETAIL_FIELD_LABEL_STATUS = AUCTION_LABEL_STATUS;
export const AUCTION_DETAIL_FIELD_LABEL_DATE = AUCTION_LABEL_STARTING_DATE;
export const AUCTION_DETAIL_FIELD_LABEL_AUCTION_ENDS = 'Ending Date';
export const AUCTION_DETAIL_FIELD_LABEL_ANTI_SNIPING = 'Anti-sniping';
export const AUCTION_DETAIL_FIELD_LABEL_STARTING_PRICE = AUCTION_LABEL_STARTING_PRICE;
export const AUCTION_DETAIL_FIELD_LABEL_MINIMUM_BID_STEP = 'Minimum bid step';
export const AUCTION_DETAIL_FIELD_LABEL_RESERVE_PRICE = 'Reserve price';
export const AUCTION_DETAIL_FIELD_LABEL_PROXY_BIDDING = 'Proxy bidding';
export const AUCTION_DETAIL_FIELD_LABEL_SOLD_FOR = 'Sold for';

export const DOCUMENT_OF_ITEM = 'item';
export const DOCUMENT_TYPE_AUDIT_LOG = 'auditLog';

/** Human labels for the document types we know about. */
export const DOCUMENT_TYPE_LABELS = {
  [DOCUMENT_TYPE_AUDIT_LOG]: 'Audit Log',
};

export const DOCUMENT_TYPE_FALLBACK_LABEL = 'Document';

/**
 * An unknown type is shown as it arrived: the portal accepts document types it
 * has never seen, and hiding one here would undo that.
 */
export const documentTypeLabel = (documentType) =>
  DOCUMENT_TYPE_LABELS[documentType] || documentType || DOCUMENT_TYPE_FALLBACK_LABEL;

/** Belongs to the auction as a whole -- an item's document stays with its item. */
export const isAuctionLevelDocument = (document) =>
  Boolean(document) && document.documentOf !== DOCUMENT_OF_ITEM;

export const AUCTION_DETAIL_FIELD_LABELS = {
  TYPE: AUCTION_DETAIL_FIELD_LABEL_TYPE,
  STATUS: AUCTION_DETAIL_FIELD_LABEL_STATUS,
  DATE: AUCTION_DETAIL_FIELD_LABEL_DATE,
  AUCTION_ENDS: AUCTION_DETAIL_FIELD_LABEL_AUCTION_ENDS,
  ANTI_SNIPING: AUCTION_DETAIL_FIELD_LABEL_ANTI_SNIPING,
  STARTING_PRICE: AUCTION_DETAIL_FIELD_LABEL_STARTING_PRICE,
  MINIMUM_BID_STEP: AUCTION_DETAIL_FIELD_LABEL_MINIMUM_BID_STEP,
  RESERVE_PRICE: AUCTION_DETAIL_FIELD_LABEL_RESERVE_PRICE,
  PROXY_BIDDING: AUCTION_DETAIL_FIELD_LABEL_PROXY_BIDDING,
  SOLD_FOR: AUCTION_DETAIL_FIELD_LABEL_SOLD_FOR,
};
